import { useState } from "react";
import { toast } from "sonner";
import { FileDown, Loader2, MapPin, Mail, Phone } from "lucide-react";
import { adminApi, errMsg } from "../../lib/api";
import { downloadBlob } from "../../lib/exportPng";
import { eur } from "../../lib/shield";

const STAGES = ["paid", "printing", "dispatched"];
const BADGE = { paid: "bg-emerald-100 text-emerald-800", printing: "bg-blue-100 text-blue-800", dispatched: "bg-purple-100 text-purple-800", awaiting_payment: "bg-amber-100 text-amber-800" };

const PrintButton = ({ order, idx, item }) => {
  const [busy, setBusy] = useState(false);
  const get = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi().get(`/admin/orders/${order.id}/items/${idx}/print.pdf`, { responseType: "blob" });
      downloadBlob(data, `${order.number}-${idx + 1}-${item.size}.pdf`);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No print artwork stored for this item." : errMsg(e));
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={get} disabled={busy || !item.print_file_id} data-testid={`admin-print-${order.number}-${idx}`}
      className="flex items-center gap-1.5 bg-slate-900 px-3 py-1.5 text-xs font-bold uppercase text-white hover:bg-slate-700 disabled:opacity-40">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Print PDF
    </button>
  );
};

const SheetButton = ({ order }) => {
  const [busy, setBusy] = useState(false);
  const get = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi().get(`/admin/orders/${order.id}/sheet.pdf`, { responseType: "blob" });
      downloadBlob(data, `${order.number}-production.pdf`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={get} disabled={busy} data-testid={`admin-sheet-${order.number}`}
      className="mt-2 inline-flex items-center gap-1.5 border-2 border-slate-900 bg-lime-300 px-2.5 py-1 text-[11px] font-black uppercase text-slate-900 hover:bg-lime-200 disabled:opacity-60">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Production sheet + label
    </button>
  );
};

export const OrderCard = ({ order, onChanged }) => {
  const c = order.customer;
  const setStatus = async (status) => {
    try { await adminApi().put(`/admin/orders/${order.id}/status`, { status }); toast.success(`${order.number} → ${status}`); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const [sending, setSending] = useState(false);
  const readyEmail = async () => {
    setSending(true);
    try { await adminApi().post(`/admin/orders/${order.id}/ready-to-collect`); toast.success(`Ready-to-collect email sent to ${c.email}`); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };
  const address = order.collect ? `Click & collect – ${order.collect_address}` : [c.address_line1, c.address_line2, c.city, c.county, c.postcode, c.country].filter(Boolean).join(", ");
  return (
    <article className="border-2 border-slate-200 bg-white p-4 hover:border-slate-900" data-testid={`admin-order-${order.number}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono-cc text-lg font-bold">{order.number}</span>
            {order.club_delivery && <span className="bg-lime-100 px-2 py-0.5 text-[10px] font-bold uppercase text-lime-800" data-testid={`admin-order-club-${order.number}`}>Club delivery</span>}{order.collect && <span className="bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800" data-testid={`admin-order-collect-${order.number}`}>Click & collect</span>}{order.coupon && <span className="bg-slate-100 px-2 py-0.5 text-[10px] font-bold" data-testid={`admin-order-coupon-${order.number}`}>{order.coupon} −{order.discount_percent}%</span>}{order.voucher && <span className="bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700" data-testid={`admin-order-voucher-${order.number}`}>Gift {order.voucher} −{eur(order.voucher_amount)}</span>}{order.delivery > 0 && <span className="text-xs text-slate-500">+ delivery {(order.delivery / 100).toFixed(2)}</span>}
            <span className={`px-2 py-0.5 text-[11px] font-bold uppercase ${BADGE[order.status] || "bg-slate-100 text-slate-700"}`} data-testid={`admin-order-status-${order.number}`}>{order.status.replace("_", " ")}</span>
          </div>
          <div className="text-xs text-slate-500">{new Date(order.created_at).toLocaleString("en-IE")}</div>
          <SheetButton order={order} />
        </div>
        <div className="text-right">
          <div className="font-cond text-2xl font-bold">{eur(order.total)}</div>
          {STAGES.includes(order.status) && (
            <select value={order.status} onChange={(e) => setStatus(e.target.value)} data-testid={`admin-order-stage-${order.number}`}
              className="border-2 border-slate-200 px-2 py-1 text-xs font-bold uppercase outline-none focus:border-slate-900">
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {order.collect && STAGES.includes(order.status) && (
            <button type="button" onClick={readyEmail} disabled={sending} data-testid={`admin-order-ready-${order.number}`}
              className="mt-2 block w-full bg-amber-400 px-2 py-1.5 text-[11px] font-black uppercase text-slate-900 hover:bg-amber-300 disabled:opacity-60">
              {sending ? "Sending…" : order.ready_emailed_at ? "Resend ready email" : "Ready to collect – email customer"}
            </button>
          )}
          {order.dispatch_emailed_at && <div className="mt-1 text-[10px] font-bold text-purple-700" data-testid={`admin-order-dispatch-at-${order.number}`}>Dispatch email sent {new Date(order.dispatch_emailed_at).toLocaleString("en-IE")}</div>}
          {order.ready_emailed_at && <div className="mt-1 text-[10px] font-bold text-amber-700" data-testid={`admin-order-ready-at-${order.number}`}>Emailed {new Date(order.ready_emailed_at).toLocaleString("en-IE")}</div>}
        </div>
      </div>
      <div className="mt-3 grid gap-4 md:grid-cols-[1fr_1.4fr]">
        <div className="space-y-1 text-sm" data-testid={`admin-order-customer-${order.number}`}>
          <div className="font-bold">{c.name}</div>
          <div className="flex items-center gap-1.5 text-slate-600"><Mail className="h-3.5 w-3.5" /> {c.email}</div>
          <div className="flex items-center gap-1.5 text-slate-600"><Phone className="h-3.5 w-3.5" /> {c.phone}</div>
          <div className="flex items-start gap-1.5 text-slate-600"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span data-testid={`admin-order-address-${order.number}`}>{address}</span></div>
        </div>
        <ul className="divide-y border-2 border-slate-100">
          {order.items.map((it, i) => (
            <li key={i} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`admin-item-${order.number}-${i}`}>
              <span className="min-w-0"><b>{it.title}</b> · {it.size_name} {it.w}×{it.h} mm · ×{it.quantity}</span>
              <PrintButton order={order} idx={i} item={it} />
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
};
